import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { markAllPortalNotificationsRead } from '../services/notifications';
import {
  applyMarkAllReadToNotificationsCache,
  countUnreadNotificationsInCache,
  getPortalNotificationsQueryKey,
  invalidatePortalNotificationsList,
} from './portalNotificationsCache';

export const useMarkAllNotificationsReadMutation = () => {
  const queryClient = useQueryClient();
  const { userRole, notificationCount, resetNotificationCount, restoreNotificationCount } =
    useAuth();

  return useMutation({
    mutationFn: async () => {
      const result = await markAllPortalNotificationsRead();
      if (!result.success) {
        throw new Error(result.message || 'Failed to mark all as read');
      }
      return result;
    },
    onMutate: async () => {
      const queryKey = getPortalNotificationsQueryKey(userRole);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData(queryKey);
      const previousNotificationCount = Math.max(
        Number(notificationCount) || 0,
        countUnreadNotificationsInCache(queryClient, userRole),
      );

      applyMarkAllReadToNotificationsCache(queryClient, userRole);
      resetNotificationCount();

      return { previous, previousNotificationCount };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(getPortalNotificationsQueryKey(userRole), context.previous);
      }
      if (context?.previousNotificationCount != null) {
        restoreNotificationCount(context.previousNotificationCount);
      }
      invalidatePortalNotificationsList(queryClient, userRole);
    },
  });
};
